import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Minus, Plus, ShieldCheck } from "lucide-react";
import SEO from "../../components/SEO";
import img99 from "../../assets/img99.jpeg";
import Testimonials from "../../components/Testimonials";

const quoteUrl = "https://compulife.org/website/655/quoter.html";
const bookingUrl = "https://book.einsured.ca/widget/bookings/ei-team";

function useInViewOnce(threshold = 0.18) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, inView];
}

const ease = "cubic-bezier(0.22, 1, 0.36, 1)";
const ENTRANCE_DUR_MULT = 1.55;
const ENTRANCE_DIST_MULT = 1.9;

const dur = (ms) => Math.round(ms * ENTRANCE_DUR_MULT);
const dist = (px) => Math.round(px * ENTRANCE_DIST_MULT);

const makeFadeUp = (inView, delayMs = 0, y = 16, durMs = 700) => {
  const Y = dist(y);
  const D = dur(durMs);

  return {
    opacity: inView ? 1 : 0,
    transform: inView ? "translateY(0px)" : `translateY(${Y}px)`,
    transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
    willChange: "transform, opacity",
  };
};

const makeFadeScale = (
  inView,
  delayMs = 0,
  scaleFrom = 0.985,
  durMs = 750
) => {
  const D = dur(durMs);
  const Y = dist(10);

  return {
    opacity: inView ? 1 : 0,
    transform: inView
      ? "translateY(0px) scale(1)"
      : `translateY(${Y}px) scale(${scaleFrom})`,
    transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
    willChange: "transform, opacity",
  };
};

function AnimatedSection({ children, className = "", threshold = 0.18 }) {
  const [ref, inView] = useInViewOnce(threshold);

  return (
    <section ref={ref} className={className}>
      {typeof children === "function" ? children(inView) : children}
    </section>
  );
}

const cardTitleClassName =
  "w-full text-lg sm:text-xl font-bold text-[#49628b] mb-3 leading-snug lg:min-h-[3.5rem]";

function StandardCardGrid({ items, icon = "check", columns = "default" }) {
  const Icon = icon === "shield" ? ShieldCheck : CheckCircle;

  const gridClass =
    columns === "three"
      ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto"
      : columns === "five"
      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 max-w-7xl mx-auto"
      : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto";

  return (
    <div className={gridClass}>
      {items.map((item, idx) => {
        const cardLink = item.href || item.to;

        return (
          <div
            key={`${item.title || idx}-${idx}`}
            className="bg-white rounded-2xl shadow-md p-5 sm:p-6 flex flex-col h-full border border-[#e8eef8] items-center text-center md:items-start md:text-left"
          >
            <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center mb-4 mx-auto md:mx-0">
              <Icon className="w-6 h-6 text-sky-500" />
            </div>

            <h3 className={cardTitleClassName}>
              {cardLink ? (
                <Link
                  to={cardLink}
                  className="inline-block rounded-sm transition-colors duration-200 hover:text-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2"
                  aria-label={`Open ${item.title}`}
                >
                  {item.title}
                </Link>
              ) : (
                item.title
              )}
            </h3>

            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
              {item.description}
            </p>
          </div>
        );
      })}
    </div>
  );
}

const LocationLifeInsurancePage = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const siteContainer = "mx-auto w-full max-w-[1380px]";

  const hasHeroSubtitle = Boolean(data.hero?.subtitle && data.hero?.description);
  const heroDescription = data.hero?.description || data.hero?.subtitle;

  useEffect(() => {
    setActiveIndex(null);

    const scrollTimer = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);

    return () => window.clearTimeout(scrollTimer);
  }, [data.slug]);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div
      style={{ fontFamily: '"Montserrat", sans-serif' }}
      className="w-full max-w-full overflow-x-hidden"
    >
      <SEO
        title={data.seo.title}
        description={data.seo.description}
        keywords={data.seo.keywords}
      />

      {/* Hero */}
      <section
        className="relative min-h-[72vh] sm:min-h-[78vh] md:min-h-[86vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 lg:px-10"
        style={{ backgroundImage: `url(${img99})` }}
        role="img"
        aria-label={`Life insurance in ${data.city} hero banner`}
      >
        <div className="absolute inset-0 bg-black/55" />

        <div
          className={`relative z-10 ${siteContainer} px-0 sm:px-2 md:px-8 lg:px-12 text-white`}
        >
          <div className="max-w-4xl mx-auto md:mx-0 text-center md:text-left">
            <h1 className="text-3xl leading-tight sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3">
              {data.hero.title}
            </h1>

           {hasHeroSubtitle && (
  <p className="text-xl leading-snug sm:text-2xl md:text-3xl lg:text-[38px] font-semibold mb-5 max-w-3xl mx-auto md:mx-0 text-white/95">
    {data.hero.subtitle}
  </p>
)}

            {heroDescription && (
              <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-8 font-medium max-w-2xl mx-auto md:mx-0 leading-relaxed text-white/95">
                {heroDescription}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-center md:justify-start">
              <a
                href={quoteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 border-2 border-sky-400 bg-sky-400 text-white rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-sky-600 hover:border-sky-600 transition-all duration-300 text-center"
              >
                Get a Free Quote
              </a>

              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 border-2 border-white bg-transparent text-white rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-white hover:text-[#49628b] transition-all duration-300 text-center"
              >
                Speak With an Advisor
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <AnimatedSection className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-white text-gray-800">
        {(inView) => (
          <div className={siteContainer}>
            <div
              style={makeFadeUp(inView, 0, 16, 720)}
              className="max-w-5xl mx-auto text-center"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-5 sm:mb-6 leading-tight">
                {data.intro.heading}
              </h2>

              {data.intro.paragraphs.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700 mb-4 sm:mb-5 last:mb-0"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Underinsured */}
      <AnimatedSection className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-[#f8fbff] text-gray-800">
        {(inView) => (
          <div className={siteContainer}>
            <div
              style={makeFadeUp(inView, 0, 16, 720)}
              className="max-w-5xl mx-auto text-center mb-8 sm:mb-12"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
                {data.underinsured.heading}
              </h2>

              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                {data.underinsured.lead}
              </p>
            </div>

            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {data.underinsured.points.map((point, idx) => {
                const title = typeof point === "string" ? null : point.title;
                const description =
                  typeof point === "string" ? point : point.description;

                return (
                  <div
                    key={`${title || description}-${idx}`}
                    style={makeFadeScale(inView, idx * 120, 0.988, 760)}
                    className="bg-white rounded-2xl shadow-md p-5 sm:p-6 flex flex-col h-full border border-[#e8eef8] items-center text-center lg:items-start lg:text-left"
                  >
                    <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center mb-4 mx-auto lg:mx-0">
                      <ShieldCheck className="w-6 h-6 text-sky-500" />
                    </div>

                    {title && (
                      <h3 className="w-full text-lg sm:text-xl font-bold text-[#49628b] mb-3 leading-snug">
                        {title}
                      </h3>
                    )}

                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                      {description}
                    </p>
                  </div>
                );
              })}
            </div>

            {data.underinsured.question && (
              <p
                style={makeFadeUp(inView, 260, 16, 720)}
                className="max-w-5xl mx-auto mt-6 sm:mt-8 text-center text-sm sm:text-base md:text-lg leading-relaxed text-gray-700"
              >
                {data.underinsured.question}
              </p>
            )}
          </div>
        )}
      </AnimatedSection>

      {/* Broker */}
      <AnimatedSection className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-white text-gray-800">
        {(inView) => (
          <div className={siteContainer}>
            <div
              style={makeFadeUp(inView, 0, 16, 720)}
              className="max-w-5xl mx-auto text-center mb-8 sm:mb-12"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
                {data.broker.heading}
              </h2>

              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                {data.broker.description}
              </p>
            </div>

            <div style={makeFadeScale(inView, 160, 0.988, 760)}>
              <StandardCardGrid
                items={data.broker.cards}
                columns={data.broker.cards.length === 3 ? "three" : "default"}
              />
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Options */}
      <AnimatedSection className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-[#f8fbff] text-gray-800">
        {(inView) => (
          <div className={siteContainer}>
            <div
              style={makeFadeUp(inView, 0, 16, 720)}
              className="max-w-5xl mx-auto text-center mb-8 sm:mb-12"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
                {data.options.heading}
              </h2>

              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                {data.options.description}
              </p>
            </div>

            <div style={makeFadeScale(inView, 160, 0.988, 760)}>
              <StandardCardGrid items={data.options.cards} />
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Coverage */}
      <AnimatedSection className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-white text-gray-800">
        {(inView) => {
          const hasCoveragePoints =
            Array.isArray(data.coverage.points) && data.coverage.points.length > 0;

          return (
            <div className={siteContainer}>
              <div
                style={makeFadeUp(inView, 0, 16, 720)}
                className="max-w-5xl mx-auto text-center"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-5 sm:mb-6 leading-tight">
                  {data.coverage.heading}
                </h2>

                <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700 mb-4">
                  {data.coverage.description}
                </p>

                {data.coverage.subDescription && (
                  <p className="text-sm sm:text-base md:text-lg leading-relaxed text-gray-700 mb-6">
                    {data.coverage.subDescription}
                  </p>
                )}
              </div>

              {hasCoveragePoints ? (
                <div
                  style={makeFadeScale(inView, 160, 0.988, 760)}
                  className="max-w-4xl mx-auto"
                >
                  <ul className="list-disc pl-6 sm:pl-8 space-y-2 text-sm sm:text-base md:text-lg leading-relaxed text-gray-700 text-left">
                    {data.coverage.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>

                  <p className="mt-7 text-center text-sm sm:text-base md:text-lg leading-relaxed text-gray-700">
                    {data.coverage.conclusion}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 text-center md:text-left mb-6 max-w-5xl mx-auto">
                    {data.coverage.methods.map((method, idx) => (
                      <div
                        key={method.title}
                        style={makeFadeScale(inView, idx * 120, 0.988, 760)}
                        className="bg-[#f8fbff] rounded-2xl shadow-sm p-5 sm:p-6 border border-[#dde7f5] h-full flex flex-col items-center text-center md:items-start md:text-left"
                      >
                        <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center mb-4 mx-auto md:mx-0">
                          <CheckCircle className="w-6 h-6 text-sky-500" />
                        </div>

                        <h3 className={cardTitleClassName}>{method.title}</h3>

                        <p className="text-sm sm:text-base leading-relaxed text-gray-700">
                          {method.description}
                        </p>
                      </div>
                    ))}
                  </div>

                  <p className="max-w-5xl mx-auto text-center text-sm sm:text-base md:text-lg leading-relaxed text-gray-700">
                    {data.coverage.conclusion}
                  </p>
                </>
              )}
            </div>
          );
        }}
      </AnimatedSection>

      {/* Why */}
      <AnimatedSection className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-[#f8fbff] text-gray-800">
        {(inView) => (
          <div className={siteContainer}>
            <div
              style={makeFadeUp(inView, 0, 16, 720)}
              className="max-w-5xl mx-auto text-center mb-8 sm:mb-12"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4 leading-tight">
                {data.why.heading}
              </h2>

              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                {data.why.description}
              </p>
            </div>

            <div style={makeFadeScale(inView, 160, 0.988, 760)}>
              <StandardCardGrid
                items={data.why.cards}
                columns={data.why.cards.length === 5 ? "five" : "default"}
              />
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 md:pt-20 md:pb-10 px-4 sm:px-6 lg:px-10 bg-gray-50">
        <div className={siteContainer}>
          <Testimonials limit={12} autoPlay={true} intervalMs={4500} />
        </div>
      </section>

      {/* FAQ */}
      <AnimatedSection className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-[#f8fbff] text-gray-800">
        {(inView) => (
          <div className={siteContainer}>
            <div
              style={makeFadeUp(inView, 0, 16, 720)}
              className="max-w-5xl mx-auto text-center mb-8 sm:mb-12"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] leading-tight">
                Frequently Asked Questions
              </h2>
            </div>

            <div
              style={makeFadeScale(inView, 160, 0.988, 760)}
              className="max-w-4xl mx-auto space-y-4"
            >
              {data.faqs.map((faq, index) => {
                const expanded = activeIndex === index;
                const panelId = `faq-panel-${data.slug}-${index}`;
                const btnId = `faq-btn-${data.slug}-${index}`;

                return (
                  <div
                    key={faq.question}
                    className="border border-[#dde7f5] rounded-xl shadow-sm transition-all bg-white overflow-hidden"
                  >
                    <button
                      id={btnId}
                      type="button"
                      className="w-full flex justify-between items-center gap-4 text-left px-4 sm:px-6 py-4 font-semibold text-sm sm:text-base md:text-lg text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e48a0] focus-visible:ring-offset-2"
                      onClick={() => toggleFAQ(index)}
                      aria-expanded={expanded}
                      aria-controls={panelId}
                    >
                      <span className="pr-2 text-left">{faq.question}</span>

                      {expanded ? (
                        <Minus
                          className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 shrink-0"
                          aria-hidden="true"
                        />
                      ) : (
                        <Plus
                          className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 shrink-0"
                          aria-hidden="true"
                        />
                      )}
                    </button>

                    {expanded && (
                      <div
                        id={panelId}
                        role="region"
                        aria-labelledby={btnId}
                        className="px-4 sm:px-6 pb-4 text-gray-700 text-sm sm:text-base leading-relaxed text-center md:text-left"
                      >
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Final CTA */}
      <AnimatedSection className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-10 bg-[#49628b] text-white">
        {(inView) => (
          <div className={siteContainer}>
            <div
              style={makeFadeScale(inView, 0, 0.988, 760)}
              className="max-w-5xl mx-auto text-center"
            >
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight">
                {data.finalCta.heading}
              </h2>

              {data.finalCta.paragraphs.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-sm sm:text-base md:text-lg leading-relaxed text-white/90 mb-4 last:mb-0 max-w-4xl mx-auto"
                >
                  {paragraph}
                </p>
              ))}

              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center px-6 sm:px-8 py-3 border-2 border-sky-400 bg-sky-400 text-white rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-sky-600 hover:border-sky-600 transition-all duration-300"
              >
                Book Your Free Consultation
              </a>
            </div>
          </div>
        )}
      </AnimatedSection>
    </div>
  );
};

export default LocationLifeInsurancePage;